import { useState, useMemo } from 'react';
import { Copy, Download, Wand2, Minimize2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Button } from '@/components/ui/Button';
import { Textarea, Field } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { copyToClipboard, download, formatBytes } from '@/lib/utils';
import { toast } from 'sonner';

const SAMPLE = `{"name":"Utility OS","tools":60,"categories":["pdf","image","text"],"meta":{"created":"2026-04","local":true}}`;

export function JsonFormatter() {
  const [input, setInput] = useState(SAMPLE);
  const [indent, setIndent] = useState(2);

  const result = useMemo(() => {
    if (!input.trim()) return { ok: true, output: '', error: null, parsed: null as unknown };
    try {
      const parsed = JSON.parse(input);
      return {
        ok: true as const,
        output: JSON.stringify(parsed, null, indent),
        error: null,
        parsed,
      };
    } catch (e) {
      return {
        ok: false as const,
        output: '',
        error: e instanceof Error ? e.message : 'Invalid JSON',
        parsed: null,
      };
    }
  }, [input, indent]);

  const compact = useMemo(() => {
    if (!result.ok || !result.parsed) return '';
    return JSON.stringify(result.parsed);
  }, [result]);

  const stats = useMemo(() => {
    if (!result.ok || !result.parsed) return null;
    const obj = result.parsed;
    function count(node: unknown): { keys: number; values: number; depth: number } {
      if (node === null || typeof node !== 'object') return { keys: 0, values: 1, depth: 0 };
      if (Array.isArray(node)) {
        let depth = 0;
        let values = 0;
        for (const x of node) {
          const c = count(x);
          values += c.values;
          depth = Math.max(depth, c.depth);
        }
        return { keys: 0, values, depth: depth + 1 };
      }
      const entries = Object.entries(node as Record<string, unknown>);
      let keys = entries.length;
      let values = 0;
      let depth = 0;
      for (const [, v] of entries) {
        const c = count(v);
        keys += c.keys;
        values += c.values;
        depth = Math.max(depth, c.depth);
      }
      return { keys, values, depth: depth + 1 };
    }
    return count(obj);
  }, [result]);

  return (
    <div>
      <ToolHeader
        toolId="json-format"
        title="JSON formatter"
        description="Format, minify, and validate JSON. Includes structural stats and live error reporting."
        badges={
          <Badge variant={result.ok ? 'success' : 'danger'}>
            {result.ok ? (
              <><CheckCircle2 size={10} /> valid</>
            ) : (
              <><AlertCircle size={10} /> invalid</>
            )}
          </Badge>
        }
        actions={
          <>
            <Button size="sm" variant="ghost" onClick={() => setInput(SAMPLE)}>
              Sample
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card padded={false} className="!p-0">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
            <div className="text-xs font-medium text-fg-muted">Input</div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={() => setInput('')}>
                Clear
              </Button>
            </div>
          </div>
          <Textarea
            mono
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Paste JSON here…"
            className="!border-0 !rounded-t-none !rounded-b-xl min-h-[420px] !bg-transparent focus-visible:!ring-0"
            invalid={!result.ok}
          />
        </Card>

        <Card padded={false} className="!p-0">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="text-xs font-medium text-fg-muted">Output</div>
              <Field label="" className="m-0">
                <select
                  value={indent}
                  onChange={(e) => setIndent(Number(e.target.value))}
                  className="bg-transparent text-xs text-fg-muted border-0 outline-none cursor-pointer pr-2"
                >
                  <option value={2}>2 spaces</option>
                  <option value={4}>4 spaces</option>
                  <option value={1}>1 space</option>
                  <option value={0}>tab</option>
                </select>
              </Field>
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  if (!result.ok) return;
                  await copyToClipboard(compact);
                  toast.success('Minified JSON copied');
                }}
                disabled={!result.ok}
              >
                <Minimize2 size={11} />
                Minify
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  if (!result.ok) return;
                  const ok = await copyToClipboard(result.output);
                  if (ok) toast.success('Copied');
                }}
                disabled={!result.ok}
              >
                <Copy size={11} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => download(result.output, 'formatted.json', 'application/json')}
                disabled={!result.ok}
              >
                <Download size={11} />
              </Button>
            </div>
          </div>
          {result.ok ? (
            <pre className="font-mono text-[13px] leading-relaxed p-4 overflow-auto min-h-[420px] max-h-[420px]">
              <code>{result.output}</code>
            </pre>
          ) : (
            <div className="p-4 min-h-[420px]">
              <div className="flex items-start gap-2 text-danger">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-medium">Invalid JSON</div>
                  <div className="text-xs text-fg-muted mt-1 font-mono">{result.error}</div>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {result.ok && stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
          <Stat label="Keys" value={stats.keys.toString()} />
          <Stat label="Values" value={stats.values.toString()} />
          <Stat label="Depth" value={stats.depth.toString()} />
          <Stat label="Original" value={formatBytes(new Blob([input]).size)} />
          <Stat
            label="Compacted"
            value={formatBytes(new Blob([compact]).size)}
            sub={
              input.length
                ? `${Math.round((1 - compact.length / input.length) * 100)}% smaller`
                : undefined
            }
          />
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card className="!p-3">
      <div className="text-2xs uppercase tracking-wider text-fg-subtle">{label}</div>
      <div className="text-lg font-semibold mt-1 font-mono">{value}</div>
      {sub && <div className="text-2xs text-fg-subtle mt-0.5">{sub}</div>}
    </Card>
  );
}

export default JsonFormatter;
